import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Select,
  Text,
  Textarea
} from '@chakra-ui/react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import Loader from '../../../components/loader'
import AbraMidiaService from '../../../services/AbraMidia/AbraMidiaService'

interface IForm {
  name: string
  note: string
}

const allowedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4']

export function AbraMidiaNewPage() {
  const abraMidiaService = new AbraMidiaService()
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [form, setForm] = useState<IForm>({
    name: '',
    note: ''
  })
  const [showLoader, setShowLoader] = useState(false)
  const [loaderMessage, setLoaderMessage] = useState('')
  const navigate = useNavigate()

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file && allowedFileTypes.includes(file.type)) {
      setSelectedFiles([...selectedFiles, file])
    }
  }

  const moveFilePosition = (curIndex: number, newIndex: number) => {
    const element = selectedFiles.splice(curIndex, 1)[0]

    selectedFiles.splice(newIndex, 0, element)

    setSelectedFiles([...selectedFiles])
  }

  const removeFile = (index: number) => {
    selectedFiles.splice(index, 1)

    setSelectedFiles([...selectedFiles])
  }

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setShowLoader(true)
    setLoaderMessage('Salvando ABRA Mídia...')

    const { data: abraMidia } = await abraMidiaService.store({
      title: form.name,
      notes: form.note
    })

    let position = 1
    for (const file of selectedFiles) {
      setLoaderMessage(`Enviando arquivo ${file.name}...`)

      await abraMidiaService.uploadFile({
        file,
        position: position++,
        abraMediaId: abraMidia.id
      })
    }

    setShowLoader(false)
    setLoaderMessage('')

    navigate('/abra-midia')
  }

  return (
    <>
      <Loader message={loaderMessage} show={showLoader} />
      <Link to="/abra-midia">
        <Text fontSize="sm" color="magenta.500" display="flex" alignItems="center">
          <i className="lni lni-arrow-left" style={{ marginRight: '5px' }}></i> Voltar
        </Text>
      </Link>
      <Heading as="h1" size="lg" marginBottom="5" textTransform="uppercase">
        Nova ABRA Mídia
      </Heading>
      <form style={{ marginBottom: '50px' }} onSubmit={submitForm}>
        <Button
          type="submit"
          position="fixed"
          bottom="5"
          right="5"
          size="lg"
          colorScheme="magenta"
          borderRadius="full"
          boxShadow="lg"
          width={16}
          height={16}
          _hover={{ boxShadow: 'xl' }}
          _active={{ boxShadow: 'md' }}
        >
          <i className="lni lni-save"></i>
        </Button>

        <FormControl isRequired mb="5">
          <FormLabel>Nome/Título</FormLabel>
          <Input type="text" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </FormControl>

        <FormControl isRequired mb="5">
          <FormLabel>Observação</FormLabel>
          <Textarea value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
        </FormControl>

        <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }} gap={{ base: 4, md: 6 }}>
          <GridItem>
            <Box borderWidth="1px" borderRadius="md" overflow="hidden" boxShadow="md" width="100%" mx="auto">
              <Flex justifyContent="center" alignItems="center" height="200px">
                <Text fontSize="9xl" fontWeight="bold" textAlign="center" color={'gray.300'}>
                  <i className="lni lni-add-files"></i>
                </Text>
              </Flex>
              <Box p={4}>
                <Input
                  type="file"
                  display="none"
                  id="file-input"
                  onChange={handleFileChange}
                  accept=".jpg, .jpeg, .png, .gif, .mp4"
                />
                <Flex justifyContent="center">
                  <label htmlFor="file-input">
                    <Button as="span" colorScheme="blue" cursor="pointer">
                      Adicionar arquivo
                    </Button>
                  </label>
                </Flex>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Formatos aceitos: JPG, PNG, GIF, MP4
                </Text>
                <Text fontSize="xs" color="gray.500" mb="18px">
                  Tamanho máximo por arquivo: 10Mb
                </Text>
              </Box>
            </Box>
          </GridItem>

          {selectedFiles.map((file, index) => (
            <GridItem key={index}>
              <Box borderWidth="1px" borderRadius="md" overflow="hidden" boxShadow="md" width="100%" mx="auto">
                {(() => {
                  if (file.type === 'video/mp4') {
                    return (
                      <Flex justifyContent="center" alignItems="center" height="200px">
                        <Text fontSize="8xl" fontWeight="bold" textAlign="center" color={'gray.500'}>
                          <i className="lni lni-video"></i>
                        </Text>
                      </Flex>
                    )
                  }

                  return (
                    <Flex
                      height="200px"
                      justifyContent="center"
                      alignItems="center"
                      boxShadow="inset 0px 0px 0px 3px rgba(0,0,0,0.1)"
                      backgroundImage={`url(${URL.createObjectURL(file)})`}
                      backgroundSize="cover"
                      backgroundPosition="center"
                      backgroundColor="#FFF"
                    ></Flex>
                  )
                })()}

                <Flex justifyContent="space-between" alignItems="top" pl={4}>
                  <Text fontSize="xl" fontWeight="bold" textAlign="center" mt={2}>
                    {file.name}
                  </Text>

                  <Select
                    size="sm"
                    width="25%"
                    value={index}
                    onChange={(event) => moveFilePosition(index, Number(event.target.value))}
                  >
                    {(() => {
                      const options = []

                      for (let i = 1; i <= selectedFiles.length; i++) {
                        options.push(
                          <option key={i} value={i - 1}>
                            {i}
                          </option>
                        )
                      }

                      return options
                    })()}
                  </Select>
                </Flex>

                <Box p={4}>
                  <Flex justifyContent="end" mt={4}>
                    <Button colorScheme="red" size="sm" mt={4} onClick={() => removeFile(index)}>
                      Remover
                    </Button>
                  </Flex>
                </Box>
              </Box>
            </GridItem>
          ))}
        </Grid>
      </form>
    </>
  )
}
